import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Paper,
} from '@mui/material';

interface Column {
  id?: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => string;
  width?: string;
}

interface Totals {
  [key: string]: number;
}

interface DashboardSummaryTableProps {
  data: Array<{ [key: string]: any }>;
  columns: Column[];
  totals: Totals;
}

const formatValue = (column: Column, value: any) =>
  column.format ? column.format(value) : value;

const DashboardSummaryTable = ({
  data,
  columns,
  totals,
}: DashboardSummaryTableProps) => {
  // Define column widths
  const columnWidths = {
    accountName: '40%',
    totalHoursAllocated: '30%',
    totalHoursUtilized: '30%',
  };

  return (
    <Box
      sx={{
        height: '88%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          overflow: 'auto',
          border: 'none',
          boxShadow: 'none',
          minHeight: 0,
        }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    bgcolor: 'white',
                    fontWeight: 'bold',
                    padding: '6px 8px',
                    width: column?.width ?? 'auto',
                  }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography
                    variant="body2"
                    textAlign="center"
                    color="text.secondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        padding: '3px 8px',
                        width: column?.width ?? 'auto',
                      }}>
                      {column.format
                        ? column.format(row[column.id])
                        : row[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Separate Total Row */}
      <Box
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
          bgcolor: 'white',
          mt: 'auto',
        }}>
        <Table>
          <TableRow
            sx={{
              '& td': {
                fontWeight: 'bold',
                padding: '6px 8px',
              },
            }}>
            <TableCell sx={{ width: columnWidths.accountName }}>
              Total
            </TableCell>
            <TableCell
              align="right"
              sx={{ width: columnWidths.totalHoursAllocated }}>
              {formatValue(columns[1], totals.totalHoursAllocated)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ width: columnWidths.totalHoursUtilized }}>
              {formatValue(columns[2], totals.totalHoursUtilized)}
            </TableCell>
          </TableRow>
        </Table>
      </Box>
    </Box>
  );
};

export default DashboardSummaryTable;
